import React, { useState, useEffect, useRef } from "react";
import { Card, Descriptions, Table, Typography, Space, Row, Col, Timeline, Divider, Alert, Badge, Button, Statistic } from "antd";
import { useReactToPrint } from "react-to-print";
import { 
  PrinterOutlined, 
  CalendarOutlined, 
  UserOutlined, 
  CheckCircleOutlined, 
  ClockCircleOutlined, 
  DollarOutlined,
  FileTextOutlined,
  TrophyOutlined,
  TeamOutlined,
  FundOutlined
} from "@ant-design/icons";
import { Sourcing, SourcingStatus } from "../interface";
import WorkflowDiagram from "../../purchase-template/components/WorkflowDiagram";
import { StatusBadge, TypeBadge, ModeBadge } from "./Badges";
import { useCustom } from "@refinedev/core";
import { GET_BIDS_BY_SOURCING_QUERY } from "../queries";

const { Title, Text } = Typography;

// Status order for timeline construction
const SOURCING_STATUS_ORDER = [
  SourcingStatus.DRAFT,
  SourcingStatus.SUBMITTED,
  SourcingStatus.APPROVED,
  SourcingStatus.PUBLISHED,
  SourcingStatus.CLOSED,
  SourcingStatus.REJECTED,
  SourcingStatus.RESULT_SUBMITTED,
  SourcingStatus.AWARD_APPROVED,
  SourcingStatus.AWARDED,
];

interface BidData {
  _id: string;
  supplierId: string;
  companyName: string;
  items: Array<{
    itemName: string;
    quantity: number;
    unitPrice: number;
    total: number;
    deliveryTime?: string;
  }>;
  compliance?: boolean;
  decision?: string;
  evaluationRemarks?: string;
  evaluationResults?: Array<{
    criteriaId: string;
    value: number;
    remarks?: string;
  }>;
}

interface OverviewTabProps {
  record: Sourcing;
}

export const OverviewTab: React.FC<OverviewTabProps> = ({ record }) => {
  const [purchaseRequest, setPurchaseRequest] = useState<any>(null);
  const [procurementPlan, setProcurementPlan] = useState<any>(null);
  const [winnerBid, setWinnerBid] = useState<BidData | null>(null);
  const [_isLoading, setIsLoading] = useState(false);

  // Calculate status index for timeline logic
  const statusIndex = SOURCING_STATUS_ORDER.indexOf(record.status);

  // Query for bids data - only enable when status is CLOSED or later
  const { refetch: refetchBids } = useCustom({
    url: '',
    method: 'get',
    meta: {
      gqlQuery: GET_BIDS_BY_SOURCING_QUERY,
      gqlVariables: {
        sourcingId: record?.id || '',
      },
    },
    queryOptions: {
      enabled: !!record?.id && statusIndex >= SOURCING_STATUS_ORDER.indexOf(SourcingStatus.CLOSED),
    },
  });

  // Load purchase request and procurement plan data (mock for now)
  useEffect(() => {
    setIsLoading(true);
    setTimeout(() => {
      // Mock purchase request data - use first PR if multiple exist
      const firstPrId = record.prIds?.[0];
      setPurchaseRequest({
        id: firstPrId,
        requestNo: `PR-${firstPrId?.slice(-6)}`,
        title: "Office Equipment Procurement",
        description: "Annual procurement of office equipment and supplies",
        status: "APPROVED",
        totalAmount: record.totalAmount * 0.8,
        currency: record.currency,
        createdBy: "John Doe",
        createdAt: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
        items: record.items.slice(0, 3),
      });

      // Mock procurement plan data
      setProcurementPlan({
        id: "PLAN-2024-001",
        planNo: "PLAN-2024-001",
        title: "2024 Annual Procurement Plan",
        fiscalYear: "2024",
        totalBudget: record.totalAmount * 1.5,
        currency: record.currency,
        status: "APPROVED",
        createdBy: "Procurement Team",
        createdAt: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString(),
      });
      setIsLoading(false);
    }, 1000);
  }, [record]);

  // Load bids data and find winner
  useEffect(() => {
    if (statusIndex >= SOURCING_STATUS_ORDER.indexOf(SourcingStatus.CLOSED) && record?.id) {
      loadBidsData();
    }
  }, [statusIndex, record?.id]);

  const loadBidsData = async () => {
    if (!record?.id || !refetchBids) return;

    try {
      const response = await refetchBids();
      const bidsData = (response as any)?.data?.data?.getBidsBySourcing || [];
      const winner = bidsData.find((bid: BidData) => bid.decision === 'Winner');
      setWinnerBid(winner || null);
    } catch (error) {
      console.error('Error loading bids data:', error);
    }
  };

  // Calculate winner total
  const winnerTotal = winnerBid?.items?.reduce((sum, item) => sum + item.total, 0) || 0;

  // Calculate estimate
  const estimate = record.items.reduce((sum, item) => {
    return sum + (item.totalPrice || (item.quantity * item.unitPrice));
  }, 0);

  // Calculate variance
  const variance = winnerTotal > 0 && estimate > 0 ? ((winnerTotal - estimate) / estimate) * 100 : 0;
  const varianceText = variance > 0 ? `↑ ${variance.toFixed(1)}%` : variance < 0 ? `↓ ${Math.abs(variance).toFixed(1)}%` : '0%';

  // Build timeline data
  const timelineItems = [
    {
      dot: <ClockCircleOutlined />,
      color: 'blue',
      children: (
        <div>
          <Text strong>Created</Text>
          <div>
            <Text type="secondary">
              {new Date(record.createdAt).toLocaleString()}
            </Text>
            {record.createdBy && (
              <div>
                <Text type="secondary">by {record.createdBy}</Text>
              </div>
            )}
          </div>
        </div>
      ),
    },
  ];

  if (record.submittedBy) {
    timelineItems.push({
      dot: <UserOutlined />,
      color: 'blue',
      children: (
        <div>
          <Text strong>Submitted</Text>
          <div>
            <Text type="secondary">
              {record.submittedAt ? new Date(record.submittedAt).toLocaleString() : 'N/A'}
            </Text>
            {record.submittedBy && (
              <div>
                <Text type="secondary">by {record.submittedBy}</Text>
              </div>
            )}
          </div>
        </div>
      ),
    });
  }

  if (record.approvedBy) {
    timelineItems.push({
      dot: <CheckCircleOutlined />,
      color: 'green',
      children: (
        <div>
          <Text strong>Approved</Text>
          <div>
            <Text type="secondary">
              {new Date(record.updatedAt).toLocaleString()}
            </Text>
            {record.approvedBy && (
              <div>
                <Text type="secondary">by {record.approvedBy}</Text>
              </div>
            )}
          </div>
        </div>
      ),
    });
  }

  if (record.publishData?.openedAt) {
    timelineItems.push({
      dot: <CalendarOutlined />,
      color: 'purple',
      children: (
        <div>
          <Text strong>Published</Text>
          <div>
            <Text type="secondary">
              {new Date(record.publishData.openedAt).toLocaleString()}
            </Text>
            {record.publishData.openedBy && (
              <div>
                <Text type="secondary">by {record.publishData.openedBy.name}</Text>
              </div>
            )}
            {record.publishData.dueDate && (
              <div>
                <Text type="secondary">
                  Deadline: {new Date(record.publishData.dueDate).toLocaleString()}
                </Text>
              </div>
            )}
          </div>
        </div>
      ),
    });
  }

  if (statusIndex >= SOURCING_STATUS_ORDER.indexOf(SourcingStatus.CLOSED)) {
    timelineItems.push({
      dot: <FileTextOutlined />,
      color: 'orange',
      children: (
        <div>
          <Text strong>Closed</Text>
          <div>
            <Text type="secondary">
              {new Date(record.updatedAt).toLocaleString()}
            </Text>
          </div>
        </div>
      ),
    });
  }

  if (statusIndex >= SOURCING_STATUS_ORDER.indexOf(SourcingStatus.RESULT_SUBMITTED)) {
    timelineItems.push({
      dot: <CheckCircleOutlined />,
      color: 'green',
      children: (
        <div>
          <Text strong>Results Submitted</Text>
          <div>
            <Text type="secondary">
              {record.resultSubmittedAt ? new Date(record.resultSubmittedAt).toLocaleString() : 'N/A'}
            </Text>
            {record.resultSubmittedBy && (
              <div>
                <Text type="secondary">by {record.resultSubmittedBy}</Text>
              </div>
            )}
          </div>
        </div>
      ),
    });
  }

  if (statusIndex >= SOURCING_STATUS_ORDER.indexOf(SourcingStatus.AWARD_APPROVED)) {
    timelineItems.push({
      dot: <CheckCircleOutlined />,
      color: 'green',
      children: (
        <div>
          <Text strong>Award Approved</Text>
          <div>
            <Text type="secondary">
              {new Date(record.updatedAt).toLocaleString()}
            </Text>
          </div>
        </div>
      ),
    });
  }

  if (statusIndex >= SOURCING_STATUS_ORDER.indexOf(SourcingStatus.AWARDED)) {
    timelineItems.push({
      dot: <TrophyOutlined />,
      color: 'gold',
      children: (
        <div>
          <Text strong>Awarded</Text>
          <div>
            <Text type="secondary">
              {record.awardedAt ? new Date(record.awardedAt).toLocaleString() : 'N/A'}
            </Text>
            {record.awardedBy && (
              <div>
                <Text type="secondary">by {record.awardedBy}</Text>
              </div>
            )}
          </div>
        </div>
      ),
    });
  }

  // React-to-print setup
  const printRef = useRef<HTMLDivElement>(null);
  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: `Sourcing Overview - ${record.sourcingNo}`,
  });

  // Add print-specific CSS for light mode
  React.useEffect(() => {
    const printStyle = document.createElement('style');
    printStyle.id = 'print-light-mode';
    printStyle.innerHTML = `
      @media print {
        /* Force light mode for print */
        * {
          background-color: white !important;
          color: black !important;
          border-color: #ddd !important;
        }
        
        /* Override any dark theme colors */
        .sourcing-overview,
        .sourcing-overview *,
        .ant-card,
        .ant-card *,
        .ant-descriptions,
        .ant-descriptions *,
        .ant-table,
        .ant-table *,
        .ant-timeline,
        .ant-timeline *,
        .ant-statistic,
        .ant-statistic * {
          background-color: white !important;
          color: black !important;
        }
        
        /* Card specific overrides */
        .ant-card {
          background-color: white !important;
          border: 1px solid #ddd !important;
          box-shadow: none !important;
        }
        
        /* Text overrides */
        .ant-typography,
        .ant-typography *,
        .ant-statistic-title,
        .ant-statistic-content {
          color: black !important;
        }
        
        /* Table overrides */
        .ant-table {
          background-color: white !important;
        }
        .ant-table-thead > tr > th {
          background-color: #f5f5f5 !important;
          color: black !important;
          border: 1px solid #ddd !important;
        }
        .ant-table-tbody > tr > td {
          background-color: white !important;
          color: black !important;
          border: 1px solid #ddd !important;
        }
        
        /* Badge overrides */
        .ant-badge-status-dot {
          background-color: #666 !important;
        }
        
        /* Timeline overrides */
        .ant-timeline-item-head {
          background-color: #666 !important;
        }
        
        /* Remove any background images or gradients */
        * {
          background-image: none !important;
        }
      }
    `;
    document.head.appendChild(printStyle);
    
    return () => {
      const existingStyle = document.getElementById('print-light-mode');
      if (existingStyle) {
        document.head.removeChild(existingStyle);
      }
    };
  }, []);



  return (
    <div className="sourcing-overview">
      
      {/* Header with Print Button */}
      <Card className="no-print">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Title level={3} style={{ margin: 0 }}>
            <FileTextOutlined style={{ marginRight: 8 }} />
            Sourcing Overview
          </Title>
          <Button 
            type="primary" 
            icon={<PrinterOutlined />} 
            onClick={handlePrint}
            size="large"
          >
            Print Overview
          </Button>
        </div>
      </Card>
      
      {/* Printable Content */}
      <div ref={printRef}>
        <Space direction="vertical" style={{ width: '100%' }}>

        {/* Executive Summary */}
        <Card title="Executive Summary">
          <Row gutter={16}>
            <Col span={8}>
              <Statistic
                title="Sourcing Number"
                value={record.sourcingNo}
                prefix={<FileTextOutlined />}
              />
            </Col>
            <Col span={8}>
              <Statistic
                title="Type"
                value={record.type}
                prefix={<TypeBadge type={record.type} />}
              />
            </Col>
            <Col span={8}>
              <div style={{ marginBottom: '16px' }}>
                <div style={{ color: '#8c8c8c', fontSize: '14px', marginBottom: '4px' }}>Status</div>
                <StatusBadge status={record.status} />
              </div>
            </Col>
          </Row>
          
          <Divider />
          
          <Descriptions column={2} bordered size="small">
            <Descriptions.Item label="Title">{record.title || 'Untitled Sourcing'}</Descriptions.Item>
            <Descriptions.Item label="Scope">
              <ModeBadge mode={record.invitationMode} />
            </Descriptions.Item>
            <Descriptions.Item label="PR References" span={2}>
              {record.prIds && record.prIds.length > 0 ? (
                <ul style={{ margin: 0, paddingLeft: '20px' }}>
                  {record.prIds.map(prId => (
                    <li key={prId}>{prId}</li>
                  ))}
                </ul>
              ) : (
                'N/A'
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Created By">{record.createdBy || 'N/A'}</Descriptions.Item>
            <Descriptions.Item label="Created Date">
              {new Date(record.createdAt).toLocaleDateString()}
            </Descriptions.Item>
            <Descriptions.Item label="Total Amount" span={2}>
              <Text strong style={{ fontSize: '16px' }}>
                {record.currency || 'ETB'} {record.totalAmount?.toLocaleString()}
              </Text>
            </Descriptions.Item>
          </Descriptions>
        </Card>

        {/* Purchase Request Information */}
        {purchaseRequest && (
          <Card title="Purchase Request Details">
            <Descriptions column={2} bordered>
              <Descriptions.Item label="PR Number">{purchaseRequest.requestNo}</Descriptions.Item>
              <Descriptions.Item label="Status">{purchaseRequest.status}</Descriptions.Item>
              <Descriptions.Item label="Title">{purchaseRequest.title}</Descriptions.Item>
              <Descriptions.Item label="Created By">{purchaseRequest.createdBy}</Descriptions.Item>
              <Descriptions.Item label="Created Date">
                {new Date(purchaseRequest.createdAt).toLocaleDateString()}
              </Descriptions.Item>
              <Descriptions.Item label="Amount">
                {purchaseRequest.currency} {purchaseRequest.totalAmount?.toLocaleString()}
              </Descriptions.Item>
              <Descriptions.Item label="Description" span={2}>
                {purchaseRequest.description}
              </Descriptions.Item>
            </Descriptions>
          </Card>
        )}

        {/* Procurement Plan Information */}
        {procurementPlan && (
          <Card title="Procurement Plan Information">
            <Descriptions column={2} bordered>
              <Descriptions.Item label="Plan Number">{procurementPlan.planNo}</Descriptions.Item>
              <Descriptions.Item label="Fiscal Year">{procurementPlan.fiscalYear}</Descriptions.Item>
              <Descriptions.Item label="Title">{procurementPlan.title}</Descriptions.Item>
              <Descriptions.Item label="Created By">{procurementPlan.createdBy}</Descriptions.Item>
              <Descriptions.Item label="Created Date">
                {new Date(procurementPlan.createdAt).toLocaleDateString()}
              </Descriptions.Item>
              <Descriptions.Item label="Total Budget">
                {procurementPlan.currency} {procurementPlan.totalBudget?.toLocaleString()}
              </Descriptions.Item>
            </Descriptions>
          </Card>
        )}

        {/* Items Summary */}
        <Card title="Items Summary">
          <Table
            dataSource={record.items}
            columns={[
              {
                title: 'Name',
                dataIndex: 'name',
                key: 'name',
              },
              {
                title: 'Description',
                dataIndex: 'description',
                key: 'description',
              },
              {
                title: 'Quantity',
                dataIndex: 'quantity',
                key: 'quantity',
              },
              {
                title: 'Unit',
                dataIndex: 'unit',
                key: 'unit',
                render: (unit: string) => unit || 'Pcs',
              },
              {
                title: 'Unit Price',
                dataIndex: 'unitPrice',
                key: 'unitPrice',
                render: (price: number) => `${record.currency || 'ETB'} ${price.toLocaleString()}`,
              },
              {
                title: 'Total Price',
                dataIndex: 'totalPrice',
                key: 'totalPrice',
                render: (total: number) => `${record.currency || 'ETB'} ${(total || 0).toLocaleString()}`,
              },
            ]}
            rowKey="id"
            pagination={false}
            size="small"
            summary={() => (
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={5}>
                  <strong>Total</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={1}>
                  <strong>
                    {record.currency || 'ETB'} {estimate.toLocaleString()}
                  </strong>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            )}
          />
        </Card>

        {/* Award Information */}
        {winnerBid && (
          <Card title="Award Information" extra={<TrophyOutlined />}>
            <Row gutter={16}>
              <Col span={8}>
                <Statistic
                  title="Winner Supplier"
                  value={winnerBid.companyName}
                  prefix={<TeamOutlined />}
                />
              </Col>
              <Col span={8}>
                <Statistic
                  title="Award Amount"
                  value={winnerTotal}
                  prefix={<DollarOutlined />}
                  suffix={record.currency || 'ETB'}
                />
              </Col>
              <Col span={8}>
                <Statistic
                  title="Variance from Estimate"
                  value={varianceText}
                  valueStyle={{ color: variance > 0 ? '#cf1322' : variance < 0 ? '#3f8600' : '#1890ff' }}
                  prefix={<FundOutlined />}
                />
              </Col>
            </Row>
            
            <Divider />
            
            <Descriptions column={2} bordered>
              <Descriptions.Item label="Total Amount">
                {record.currency || 'ETB'} {winnerTotal.toLocaleString()}
              </Descriptions.Item>
              <Descriptions.Item label="Estimate">
                {record.currency || 'ETB'} {estimate.toLocaleString()}
              </Descriptions.Item>
              <Descriptions.Item label="Savings/Overrun">
                <Text style={{ color: variance > 0 ? '#cf1322' : variance < 0 ? '#3f8600' : '#1890ff' }}>
                  {record.currency || 'ETB'} {Math.abs(winnerTotal - estimate).toLocaleString()} ({varianceText})
                </Text>
              </Descriptions.Item>
              <Descriptions.Item label="Award Date">
                {record.awardedAt ? new Date(record.awardedAt).toLocaleDateString() : 'N/A'}
              </Descriptions.Item>
            </Descriptions>

            {/* Winner Details Table */}
            <Divider />
            <Title level={5}>Winner Bid Details</Title>
            <Table
              dataSource={winnerBid.items}
              columns={[
                {
                  title: 'Item Name',
                  dataIndex: 'itemName',
                  key: 'itemName',
                },
                {
                  title: 'Quantity',
                  dataIndex: 'quantity',
                  key: 'quantity',
                },
                {
                  title: 'Unit Price',
                  dataIndex: 'unitPrice',
                  key: 'unitPrice',
                  render: (price: number) => `${record.currency || 'ETB'} ${price.toLocaleString()}`,
                },
                {
                  title: 'Total',
                  dataIndex: 'total',
                  key: 'total',
                  render: (total: number) => `${record.currency || 'ETB'} ${total.toLocaleString()}`,
                },
                {
                  title: 'Delivery Time',
                  dataIndex: 'deliveryTime',
                  key: 'deliveryTime',
                },
              ]}
              rowKey={(item, index) => `${item.itemName}-${index}`}
              pagination={false}
              size="small"
              summary={() => (
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0} colSpan={3}>
                    <strong>Total</strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={1}>
                    <strong>{record.currency || 'ETB'} {winnerTotal.toLocaleString()}</strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={2}></Table.Summary.Cell>
                </Table.Summary.Row>
              )}
            />
          </Card>
        )}

        {/* Approval Workflow */}
        {(record.approvals && record.approvals.length > 0) && (
          <Card title="Approval Workflow">
            <Space direction="vertical" style={{ width: '100%' }}>
              {record.approvals.map((approval, index) => (
                <Card 
                  key={index} 
                  size="small" 
                  style={{ 
                    marginBottom: '8px',
                    backgroundColor: approval.stepStatus === 'APPROVED' ? 'var(--ant-success-bg-color)' : 
                                     approval.stepStatus === 'REJECTED' ? 'var(--ant-error-bg-color)' : undefined,
                    borderColor: approval.stepStatus === 'APPROVED' ? 'var(--ant-success-color)' : 
                                approval.stepStatus === 'REJECTED' ? 'var(--ant-error-color)' : undefined
                  }}
                  bodyStyle={{
                    backgroundColor: 'transparent'
                  }}
                >
                  <Row justify="space-between" align="middle">
                    <Col>
                      <Space>
                        <Badge 
                          status={approval.stepStatus === 'APPROVED' ? 'success' : 
                                 approval.stepStatus === 'REJECTED' ? 'error' : 'processing'} 
                        />
                        <Text strong>
                          Step {approval.order}: {approval.stepName || approval.roleSlug}
                        </Text>
                      </Space>
                    </Col>
                    <Col>
                      <Text type="secondary">
                        {approval.stepStatus} {approval.approvedBy && `by ${approval.approvedBy}`}
                      </Text>
                    </Col>
                  </Row>
                  {approval.remarks && (
                    <div style={{ marginTop: 8 }}>
                      <Text type="secondary">Remarks: {approval.remarks}</Text>
                    </div>
                  )}
                </Card>
              ))}
            </Space>
          </Card>
        )}

        {/* Award Approval Workflow */}
        {(record.awardApprovals && record.awardApprovals.length > 0) && (
          <Card title="Award Approval Workflow">
            <Space direction="vertical" style={{ width: '100%' }}>
              {record.awardApprovals.map((approval, index) => (
                <Card 
                  key={index} 
                  size="small" 
                  style={{ 
                    marginBottom: '8px',
                    backgroundColor: approval.stepStatus === 'APPROVED' ? 'var(--ant-success-bg-color)' : 
                                     approval.stepStatus === 'REJECTED' ? 'var(--ant-error-bg-color)' : undefined,
                    borderColor: approval.stepStatus === 'APPROVED' ? 'var(--ant-success-color)' : 
                                approval.stepStatus === 'REJECTED' ? 'var(--ant-error-color)' : undefined
                  }}
                  bodyStyle={{
                    backgroundColor: 'transparent'
                  }}
                >
                  <Row justify="space-between" align="middle">
                    <Col>
                      <Space>
                        <Badge 
                          status={approval.stepStatus === 'APPROVED' ? 'success' : 
                                 approval.stepStatus === 'REJECTED' ? 'error' : 'processing'} 
                        />
                        <Text strong>
                          Award Step {approval.order}: {approval.stepName || approval.roleSlug}
                        </Text>
                      </Space>
                    </Col>
                    <Col>
                      <Text type="secondary">
                        {approval.stepStatus} {approval.approvedBy && `by ${approval.approvedBy}`}
                      </Text>
                    </Col>
                  </Row>
                  {approval.remarks && (
                    <div style={{ marginTop: 8 }}>
                      <Text type="secondary">Remarks: {approval.remarks}</Text>
                    </div>
                  )}
                </Card>
              ))}
            </Space>
          </Card>
        )}

        {/* Publishing Information */}
        {record.publishData && (
          <Card title="Publishing Information">
            <Descriptions column={2} bordered>
              <Descriptions.Item label="Portal Title">{record.publishData.title}</Descriptions.Item>
              <Descriptions.Item label="Status">{record.publishData.status}</Descriptions.Item>
              <Descriptions.Item label="Is Public">{record.publishData.isPublic ? 'Yes' : 'No'}</Descriptions.Item>
              <Descriptions.Item label="Due Date">
                {record.publishData.dueDate ? new Date(record.publishData.dueDate).toLocaleString() : 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Template">{record.publishData.template?.name}</Descriptions.Item>
              <Descriptions.Item label="Bidding Type">
                {record.publishData.biddingType?.toUpperCase()}
              </Descriptions.Item>
              <Descriptions.Item label="Description" span={2}>
                {record.publishData.description}
              </Descriptions.Item>
            </Descriptions>
          </Card>
        )}

        {/* Timeline */}
        <Card title="Sourcing Timeline">
          <Timeline
            items={timelineItems}
          />
        </Card>

        {/* External Workflow (if applicable) */}
        {record.externalWorkflow && record.approvalType === 'EXTERNAL' && (
          <Card title="External Approval Workflow">
            <Alert
              message="External Approval Flow"
              description="This sourcing uses an external approval workflow system."
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />
            <WorkflowDiagram flow={record.externalWorkflow} />
          </Card>
        )}
        </Space>
      </div>
    </div>
  );
};